import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Play, X } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';

export default function VideoSection() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const sectionRef = useRef(null);
  
  // YouTube video ID - can be easily updated when you have the video
  // Format: https://www.youtube.com/watch?v=VIDEO_ID or https://youtu.be/VIDEO_ID
  const youtubeVideoId = "mJ88oglLTVM"; // MYeAI Trading Compass video
  
  // Thumbnail image (using trading terminal image)
  const thumbnailImage = "https://images.unsplash.com/photo-1748439146117-31978164865d?auto=format&fit=crop&w=1200&q=80";

  const handlePlayInline = () => {
    setIsPlaying(true);
  };

  const handleOpenFullscreen = () => {
    setVideoOpen(true);
    setIsPlaying(false);
  };

  return (
    <>
      <section 
        id="video-section" 
        ref={sectionRef}
        className="py-10 md:py-16 bg-slate-50"
      >
        <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.4 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#337AF7] tracking-tight mb-4">
              See Trading Compass in Action
            </h2>
            <p className="text-base md:text-lg text-[#162959]/70 max-w-2xl mx-auto">
              Watch how we enforce discipline before execution
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            {/* Video Player */}
            <div
              className="relative w-full aspect-video rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-lg group"
              data-testid="video-container"
            >
              {/* Show YouTube embed when playing */}
              {isPlaying ? (
                <iframe
                  src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&rel=0`}
                  title="Trading Compass Demo Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              ) : (
                /* Thumbnail overlay (shown when not playing) */
                <div 
                  className="absolute inset-0 cursor-pointer"
                  onClick={handlePlayInline}
                >
                  <img 
                    src={thumbnailImage}
                    alt="Trading Compass video thumbnail"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
                  
                  {/* Play Button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#337AF7] flex items-center justify-center group-hover:bg-[#2563eb] group-hover:scale-110 transition-all shadow-lg">
                      <Play className="h-10 w-10 md:h-12 md:w-12 text-white fill-white ml-1" />
                    </div>
                  </div>

                  {/* Duration Badge */}
                  <div className="absolute top-4 right-4 bg-black/70 backdrop-blur px-3 py-1 rounded-lg text-sm text-white font-medium">
                    1:07
                  </div>
                </div>
              )}

              {/* Fullscreen Button (always visible) */}
              <button
                onClick={handleOpenFullscreen}
                className="absolute bottom-4 right-4 z-10 px-3 py-1.5 rounded-lg bg-black/60 backdrop-blur text-sm text-white font-medium hover:bg-black/80 transition-colors"
                data-testid="video-fullscreen-button"
              >
                Watch Fullscreen
              </button>
            </div>

            <p className="mt-6 text-center text-sm text-[#162959]/50">
              Watch how Trading Compass validates your trades before execution
            </p>
          </motion.div>
        </div>
      </section>

      {/* Video Modal (Fullscreen) */}
      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent 
          className="max-w-5xl w-full p-0 bg-black border-0 overflow-hidden"
          data-testid="video-modal"
        >
          <button
            onClick={() => setVideoOpen(false)}
            className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center hover:bg-white/20 transition-colors"
            aria-label="Close video"
          >
            <X className="h-6 w-6 text-white" />
          </button>

          <div className="relative w-full aspect-video">
            {videoOpen && (
              <iframe
                src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&rel=0`}
                title="Trading Compass Demo Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
